import orgamax from 'ampersand-app'
import React, { useMemo, useState } from 'react'
import { makeStyles } from '@mui/styles'
import PropTypes from 'prop-types'
import { Document, Page } from 'react-pdf'
import clsx from 'clsx'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconMultiPages from '@assets/images/icons/multiPages.svg'
import IconPencil from '@assets/images/icons/pencil.svg'
import { SquareIconButton } from '@/modules/base/components/buttons/index'
import lang from '@/lang'

const useStyles = makeStyles(() => ({
  container: {
    height: ({ height }) => height,
  },
  pdfDocument: ({ pageScale, pageVertical }) => ({
    display: 'flex',
    minHeight: '100%',
    justifyContent: 'center',
    alignItems: pageScale <= 1 && 'center',
    flexDirection: pageVertical ? 'column' : 'row',
  }),
  multiPageIcon: {
    position: 'absolute',
    top: 15,
    left: 15,
  },
  editButton: {
    position: 'absolute',
    bottom: 15,
    right: 15,
  },
  defaultPage: {
    height: '100%',
    aspectRatio: 0.70,
  },
  pdfPage: ({ renderMode, pageVertical }) => ({
    marginLeft: '10px',
    marginRight: '10px',
    boxShadow: renderMode !== 'svg' && '3px 3px 5px 5px #ccc',
    marginTop: pageVertical && 10,
    backgroundColor: 'unset!important',

    '&:first-of-type': {
      marginLeft: !pageVertical && 'auto',
      marginTop: 0,
    },
    '&:last-of-type': {
      marginRight: !pageVertical && 'auto',
    },
    '& .react-pdf__Page__svg': {
      backgroundColor: 'unset!important',
    },
  }),
}))

function LetterPaperThumbnail(props) {
  const {
    id,
    url,
    className,
    showMaxPages = 1,
    showEdit = false,
    onEditClick,
    height = 400,
    width,
    pageScale = 1,
    renderMode = 'canvas',
    renderTextLayer = false,
    renderAnnotationLayer = false,
    forceFileReload,
    pageVertical = false,
  } = props
  const classes = useStyles({ renderMode, pageVertical, pageScale, height })
  const [numPages, setNumPages] = useState(0)

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages)
  }

  const file = useMemo(() => ({ url }), [url, forceFileReload])
  const documentOptions = useMemo(() => ({
    cMapUrl: 'assets/cmaps/',
    cMapPacked: true,
    httpHeaders: {
      authorization: `Bearer ${orgamax?.user?.token}`,
    },
  }), [orgamax.user.token])

  const pages = useMemo(() => {
    const result = []

    const pagesToShow = showMaxPages === -1 ? numPages : Math.min(numPages, showMaxPages)

    for (let page = 1; page <= pagesToShow; page++) {
      result.push(
        <Page key={`page-${page}`} loading="" renderTextLayer={renderTextLayer} renderAnnotationLayer={renderAnnotationLayer} scale={pageScale} className={classes.pdfPage} height={height} width={width} pageNumber={page}>
          {showMaxPages === 1 && numPages > 1 && (
            <SVGIcon svg={IconMultiPages} customClass={classes.multiPageIcon} height='20px' width='20px' />
          )}
          {showEdit && (
            <div className={classes.editButton}>
              <SquareIconButton size='medium' onClick={onEditClick} icon={IconPencil} tooltip={lang.generics.edit} />
            </div>
          )}
        </Page>,
      )
    }
    return result
  }
  , [numPages, showMaxPages, width, height, pageScale])

  return (
    <div id={id} className={clsx(classes.container, className)}>
      {url
        ? <Document
          key={forceFileReload}
          file={file.url}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={(error) => console.error(error)}
          className={classes.pdfDocument}
          loading={lang.documents.fileLoaderText + ' ...'}
          renderMode={renderMode}
          options={documentOptions}>
          {pages}
        </Document>
        : <div className={clsx(classes.defaultPage)} />
      }
    </div>

  )
}

LetterPaperThumbnail.propTypes = {
  id: PropTypes.string,
  url: PropTypes.any,
  height: PropTypes.any,
  width: PropTypes.any,
  className: PropTypes.string,
  showMaxPages: PropTypes.number,
  forceFileReload: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  pageScale: PropTypes.number,
  showEdit: PropTypes.bool,
  pageVertical: PropTypes.bool,
  renderTextLayer: PropTypes.bool,
  renderMode: PropTypes.string,
}

export default React.memo(LetterPaperThumbnail)
